import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { ContactFacilityDto, ContactsOverviewDto } from '@his/shared';
import { get, put } from '@/lib/api';
import { DataTable, PageHeader } from '@/components/ui/layout';
import { Badge, Button, Card, CardContent, CardDescription, CardHeader, CardTitle, Field, Select } from '@/components/ui/primitives';
import { OwnerConfirmDialog } from './OwnerConfirm';
import { useOwnerCapability } from './owner-nav';

function AssignContactDialog({
  facility,
  contacts,
  onClose,
}: {
  facility: ContactFacilityDto;
  contacts: ContactsOverviewDto['contacts'];
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const active = contacts.filter((contact) => contact.isActive);
  const [contactUserId, setContactUserId] = useState(active[0]?.id ?? '');
  return facility ? (
    <OwnerConfirmDialog
      open
      onClose={onClose}
      title={`Assign a contact to ${facility.name}`}
      description="The contact person answers this facility's tickets and renewals in the client portal."
      confirmLabel="Assign"
      extraValid={contactUserId.length > 0}
      successMessage="Contact assigned"
      action={(payload) => put(`/platform/tenants/${facility.id}/contact`, { ...payload, contactUserId })}
      onDone={() => {
        void queryClient.invalidateQueries({ queryKey: ['platform-contacts'] });
        onClose();
      }}
    >
      <Field label="Contact person">
        <Select value={contactUserId} onChange={(event) => setContactUserId(event.target.value)}>
          {active.map((contact) => (
            <option key={contact.id} value={contact.id}>
              {contact.name} — {contact.email}
            </option>
          ))}
        </Select>
      </Field>
    </OwnerConfirmDialog>
  ) : null;
}

export function ContactsPage() {
  const canAssign = useOwnerCapability('tenant.contact.update');
  const [assigning, setAssigning] = useState<ContactFacilityDto | null>(null);
  const overview = useQuery({ queryKey: ['platform-contacts'], queryFn: () => get<ContactsOverviewDto>('/platform/contacts') });
  const contacts = overview.data?.contacts ?? [];

  return (
    <>
      <PageHeader title="Contacts" description="Who looks after each facility's tickets and renewals. Facilities nobody looks after come first." />

      <Card>
        <CardHeader>
          <CardTitle className="text-sm">Nobody assigned</CardTitle>
          <CardDescription>
            {overview.data
              ? `${overview.data.unassignedTotal} facilit${overview.data.unassignedTotal === 1 ? 'y has' : 'ies have'} no contact person${
                  overview.data.unassignedTotal > overview.data.unassigned.length ? `; the first ${overview.data.unassigned.length} are listed` : ''
                }.`
              : ' '}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <DataTable
            rows={overview.data?.unassigned}
            loading={overview.isLoading} refreshing={overview.isFetching}
            error={overview.error}
            rowKey={(row) => row.id}
            empty="Every facility has a contact person."
            columns={[
              { header: 'Facility', sort: (row) => row.name, cell: (row) => <Link className="underline-offset-2 hover:underline" to={`/owner/tenants/${row.id}`}>{row.name}</Link> },
              { header: 'Subdomain', sort: (row) => row.slug, cell: (row) => <span className="font-mono text-xs">{row.slug}</span> },
              {
                header: '',
                align: 'right',
                cell: (row) =>
                  canAssign ? (
                    <Button size="sm" variant="outline" disabled={!contacts.some((contact) => contact.isActive)} onClick={() => setAssigning(row)}>
                      Assign
                    </Button>
                  ) : null,
              },
            ]}
          />
        </CardContent>
      </Card>

      <Card className="mt-4">
        <CardHeader>
          <CardTitle className="text-sm">Contact persons</CardTitle>
          <CardDescription>Accounts are created on the Platform staff page with the contact person role.</CardDescription>
        </CardHeader>
        <CardContent>
          <DataTable
            rows={overview.data?.contacts}
            loading={overview.isLoading} refreshing={overview.isFetching}
            error={overview.error}
            rowKey={(row) => row.id}
            empty="No contact persons yet."
            columns={[
              {
                header: 'Contact',
                sort: (row) => row.name,
                cell: (row) => (
                  <div>
                    <p className="font-medium">
                      {row.name} {!row.isActive && <Badge variant="muted">Deactivated</Badge>}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {row.email}
                      {row.phone ? ` · ${row.phone}` : ''}
                    </p>
                  </div>
                ),
              },
              {
                header: 'Facilities',
                sort: (row) => row.facilities.length,
                cell: (row) =>
                  row.facilities.length === 0 ? (
                    <span className="text-muted-foreground">None</span>
                  ) : (
                    <div className="flex flex-wrap gap-1">
                      {row.facilities.map((facility) => (
                        <Link key={facility.id} to={`/owner/tenants/${facility.id}`}>
                          <Badge variant="secondary">{facility.name}</Badge>
                        </Link>
                      ))}
                    </div>
                  ),
              },
            ]}
          />
        </CardContent>
      </Card>

      {assigning && <AssignContactDialog facility={assigning} contacts={contacts} onClose={() => setAssigning(null)} />}
    </>
  );
}
